import React, { useEffect, useState, useCallback, useRef } from "react";

import CreatableSelect from "react-select/creatable";
import Select from "react-select";

import { FieldProps, getIn } from "formik";
import { FormGroup } from "react-bootstrap";
import ErrorMessage from "src/views/Docuclipper/ErrorMessage";

interface Option {
  readonly label: string;
  readonly value: string;
}

type Props = {
  type:
    | "customer"
    | "vendor"
    | "account"
    | "category"
    | "service"
    | "tax"
    | "integration";
  value: Option | null;
  createFn?: (newValue: string) => Promise<{ name: string; id?: string; value?: string }>;
  defaultOptions: Option[];
  onChange: (value: any, label?: string) => any;
  isInvalid?: boolean;
  loadOptions?: (inputValue: string) => Promise<Option[]>;
};

export const QboSelect: React.FC<Props> = ({
  createFn,
  defaultOptions,
  onChange,
  type,
  value,
  isInvalid,
  loadOptions,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const [selectedValue, setSelectedValue] = useState<Option | null>(value);

  // Resync from the parent only when the underlying id differs from what we
  // already show. After the user picks (or creates) an item we set selectedValue
  // with the chosen label; the parent then re-renders with a `value` whose label
  // may be stale or unresolved (the id updated in Formik but the *Name field /
  // async options list hasn't caught up, so the label resolves to blank). If we
  // blindly reseeded we'd revert the visible selection to that blank/old label
  // until a page refresh. Keeping our own option when the id matches preserves
  // the label the user just selected, while still resyncing on a real id change
  // (switching invoices/lines, external reset).
  useEffect(() => {
    setSelectedValue((prev) =>
      prev && prev.value === value?.value ? prev : value
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.value]);

  useEffect(() => {
    setOptions(defaultOptions);
  }, [defaultOptions]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (loadOptionsTimeoutRef.current) {
        clearTimeout(loadOptionsTimeoutRef.current);
      }
    };
  }, []);

  const handleCreate = async (inputValue: string) => {
    if (!createFn) return;
    
    setIsLoading(true);
    try {
      const rsp = await createFn(inputValue);
      const newOption = { label: rsp.name, value: rsp.value || rsp.id };
      setOptions((prev) => [newOption, ...prev]);
      setSelectedValue(newOption);
      // Pass both value and label so parent can use the name immediately
      onChange(newOption.value, newOption.label);
    } catch (err) {
      console.error("Error creating item:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const commonStyles = {
    control: (base, state) => ({
      ...base,
      border: isInvalid ? "1px solid #e55353 !important" : "1px solid #ced4da",
      boxShadow: "none",
      "&:hover": {
        border: isInvalid
          ? "1px solid #e55353 !important"
          : "1px solid #ced4da",
      },
    }),
    // Size the menu to its longest option instead of inheriting the (often
    // narrow, e.g. the tax-rate column) control width, so account / tax-rate
    // names aren't clipped to the point of being unreadable. minWidth keeps it
    // at least as wide as the control; maxWidth stops a runaway-wide menu.
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      width: "max-content",
      minWidth: "100%",
      maxWidth: "min(480px, 90vw)",
    }),
    // Wrap long option labels rather than truncating them.
    option: (provided) => ({
      ...provided,
      whiteSpace: "normal",
      wordBreak: "break-word",
    }),
    // Keep the portalled menu above everything (modals, sticky headers).
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
  };

  // Render the menu in a portal so it escapes any overflow:hidden / scroll
  // container (table cells, the invoice card) that would otherwise clip a menu
  // wider than its narrow column.
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : undefined;

  const loadOptionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Tracks the user's current search term so async-default seeding doesn't wipe
  // typed results. Empty string means "not searching" → safe to show defaults.
  const activeTermRef = useRef("");

  // When we have loadOptions: drive options ourselves via onInputChange so the backend is always called when the user types.
  // Do NOT reseed asyncOptions from defaultOptions on every parent render — that wipes
  // the user's typed search results mid-typing (loadOptions is a fresh function ref each
  // parent render, so a useEffect keyed on it fires constantly). Defaults are seeded
  // once at mount and re-applied only when the input is cleared (see handleInputChange).
  const [asyncOptions, setAsyncOptions] = useState<Option[]>(defaultOptions);

  // Seed the visible list from the parent's async-loaded defaults once they
  // arrive (QBO/Xero vendor/customer/account lists load after mount), so opening
  // the menu shows existing items WITHOUT typing. Guard on activeTermRef so a
  // parent re-render mid-search doesn't snap the menu back to the full list.
  useEffect(() => {
    if (!activeTermRef.current) {
      setAsyncOptions(defaultOptions ? defaultOptions.slice(0, 50) : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOptions]);

  const handleInputChange = useCallback(
    (inputValue: string) => {
      if (!loadOptions) return;
      if (loadOptionsTimeoutRef.current) {
        clearTimeout(loadOptionsTimeoutRef.current);
        loadOptionsTimeoutRef.current = null;
      }
      const term = (inputValue || "").trim();
      activeTermRef.current = term;
      if (!term) {
        setAsyncOptions(defaultOptions ? defaultOptions.slice(0, 50) : []);
        return;
      }
      loadOptionsTimeoutRef.current = setTimeout(() => {
        loadOptionsTimeoutRef.current = null;
        loadOptions(term)
          .then((opts) => setAsyncOptions(opts || []))
          .catch(() => setAsyncOptions([]));
      }, 300);
    },
    [loadOptions, defaultOptions]
  );

  // Get the type label for create option
  const getTypeLabel = () => {
    switch (type) {
      case "customer":
        return "customer";
      case "vendor":
        return "vendor";
      case "account":
        return "account";
      case "category":
        return "category";
      case "service":
        return "service/item";
      case "tax":
        return "tax code";
      default:
        return "item";
    }
  };

  // loadOptions + createFn: use CreatableSelect and call backend from onInputChange so the request is always sent when typing
  if (loadOptions && createFn) {
    return (
      <CreatableSelect
        placeholder={`Type to search or create new ${getTypeLabel()}`}
        {...rest}
        isClearable
        isDisabled={isLoading}
        isLoading={isLoading}
        options={asyncOptions}
        filterOption={() => true}
        onInputChange={handleInputChange}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(
            newValue ? newValue.value : null,
            newValue ? newValue.label : undefined
          );
        }}
        onCreateOption={handleCreate}
        value={selectedValue}
        styles={commonStyles}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        formatCreateLabel={(val) => `+ Create new ${getTypeLabel()}: "${val}"`}
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.length < 2
            ? "Type at least 2 characters to search"
            : `No ${getTypeLabel()} found - type to create`
        }
      />
    );
  }

  // loadOptions only (no create): use Select and call backend from onInputChange
  if (loadOptions) {
    return (
      <Select
        placeholder="Type to search"
        {...rest}
        isClearable
        options={asyncOptions}
        filterOption={() => true}
        onInputChange={handleInputChange}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(
            newValue ? newValue.value : null,
            newValue ? newValue.label : undefined
          );
        }}
        value={selectedValue}
        styles={commonStyles}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        noOptionsMessage={({ inputValue }) =>
          !inputValue || inputValue.length < 2
            ? "Type at least 2 characters to search"
            : "No options found"
        }
      />
    );
  }

  // Use CreatableSelect if createFn is provided (no async loading)
  if (createFn) {
    return (
      <CreatableSelect
        placeholder={`Type to search or create new ${getTypeLabel()}`}
        {...rest}
        isClearable
        isDisabled={isLoading}
        isLoading={isLoading}
        onChange={(newValue) => {
          setSelectedValue(newValue ? newValue : null);
          onChange(
            newValue ? newValue.value : null,
            newValue ? newValue.label : undefined
          );
        }}
        onCreateOption={handleCreate}
        options={options}
        value={selectedValue}
        formatCreateLabel={(val) => `+ Create new ${getTypeLabel()}: "${val}"`}
        styles={commonStyles}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
      />
    );
  }

  // Default: regular Select (no create, no async)
  return (
    <Select
      placeholder="Type to search"
      {...rest}
      isClearable
      onChange={(newValue) => {
        setSelectedValue(newValue ? newValue : null);
        onChange(newValue ? newValue.value : null);
      }}
      options={options}
      value={selectedValue}
      styles={commonStyles}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
    />
  );
};

export const FormikQboSelect = ({
  createFn,
  onChangeFn,
  type,
  field, // { name, value, onChange, onBlur }
  form: { touched, errors, setFieldValue }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  value,
  loadOptions,
  ...props
}: FieldProps & {
  label: string;
  options: Array<{ value: string; label: string }>;
  createFn?: any;
  onChangeFn?: any;
  type:
    | "customer"
    | "vendor"
    | "account"
    | "category"
    | "service"
    | "tax"
    | "integration";
  value: any;
  style?: React.CSSProperties;
  dataCy?: string;
  loadOptions?: (
    inputValue: string
  ) => Promise<Array<{ value: string; label: string }>>;
}) => {
  const { options } = props;
  const isInvalid = getIn(touched, field.name) && getIn(errors, field.name);
  const error = getIn(errors, field.name);

  return (
    <FormGroup
      style={props.style ? props.style : {}}
      data-cy={props.dataCy ? props.dataCy : ""}
    >
      {props.label && <label htmlFor={field.name}>{props.label}</label>}
      <QboSelect
        {...field}
        {...props}
        defaultOptions={options}
        value={value}
        onChange={(option, label) => {
          setFieldValue(field.name, option ? option : "");
          // Also write the paired *Name field (e.g. categoryXeroEdited ->
          // categoryXeroEditedName). The line-item grid renders each select's
          // label from Formik values, and an id alone can't resolve a label for
          // a just-selected/created item that isn't in the async options list
          // yet — and the <Field> key includes the id, so it remounts on change
          // and re-reads the (otherwise stale/blank) name from Formik. Writing
          // the name here makes the chosen label show immediately instead of
          // only after a page reload.
          if (/Edited$/.test(field.name)) {
            setFieldValue(`${field.name}Name`, option && label != null ? label : "");
          }
          if (onChangeFn) {
            // Pass label as second argument when available (for newly created items)
            onChangeFn(option, label);
          }
        }}
        createFn={createFn}
        type={type}
        isInvalid={!!isInvalid}
        loadOptions={loadOptions}
      />
      {isInvalid && <ErrorMessage>{error}</ErrorMessage>}
    </FormGroup>
  );
};
